import { useId } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MapPin, Plus, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const ADDRESS_TYPES = ['billing', 'service', 'other'];

export const newAddress = () => ({
  id: uuidv4(), type: 'other', line1: '', line2: '', city: '', state: '',
  postal_code: '', country: 'US', is_primary: false,
});

/**
 * Editor for an embedded list of Address value objects. Shared by the Person,
 * Organization, and Client forms. `value` is the address array; `onChange`
 * receives the next array.
 */
const AddressEditor = ({ value = [], onChange, label = 'Addresses' }) => {
  // Unique per editor instance so two AddressEditors open at once (e.g. an org
  // form plus an inline new-person form) don't share one radio group.
  const groupName = useId();
  const add = () => onChange([...value, { ...newAddress(), is_primary: value.length === 0 }]);
  const update = (id, field, v) => onChange(value.map((a) => (a.id === id ? { ...a, [field]: v } : a)));
  const remove = (id) => onChange(value.filter((a) => a.id !== id));
  const setPrimary = (id) => onChange(value.map((a) => ({ ...a, is_primary: a.id === id })));

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium flex items-center gap-2"><MapPin size={16} /> {label}</h4>
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="add-address-btn">
          <Plus size={14} /> Add address
        </Button>
      </div>
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">No addresses yet.</p>
      ) : (
        <div className="space-y-3">
          {value.map((a) => (
            <div key={a.id} className="relative border rounded-md p-3 pt-8 sm:pt-3" data-testid={`address-row-${a.id}`}>
              <button
                type="button"
                className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                onClick={() => remove(a.id)}
                aria-label="Remove address"
              >
                <X size={16} />
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Type</Label>
                  <select className="w-full border rounded-md px-2 py-2 text-sm bg-background" value={a.type} onChange={(e) => update(a.id, 'type', e.target.value)} aria-label="Address type">
                    {ADDRESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <Label className="text-xs text-muted-foreground">Address line 1</Label>
                  <Input placeholder="123 Main St" value={a.line1} onChange={(e) => update(a.id, 'line1', e.target.value)} aria-label="Address line 1" />
                </div>
                <div className="sm:col-span-2 lg:col-span-2">
                  <Label className="text-xs text-muted-foreground">Line 2</Label>
                  <Input placeholder="Suite 400 (optional)" value={a.line2} onChange={(e) => update(a.id, 'line2', e.target.value)} aria-label="Address line 2" />
                </div>
                <div className="sm:col-span-2 lg:col-span-2">
                  <Label className="text-xs text-muted-foreground">City</Label>
                  <Input placeholder="City" value={a.city} onChange={(e) => update(a.id, 'city', e.target.value)} aria-label="City" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">State</Label>
                  <Input placeholder="State" value={a.state} onChange={(e) => update(a.id, 'state', e.target.value)} aria-label="State" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">ZIP</Label>
                  <Input placeholder="ZIP" value={a.postal_code} onChange={(e) => update(a.id, 'postal_code', e.target.value)} aria-label="Postal code" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer sm:col-span-2 lg:col-span-2 lg:self-end lg:pb-2">
                  <input type="radio" name={groupName} checked={!!a.is_primary} onChange={() => setPrimary(a.id)} aria-label="Set as primary address" />
                  Primary address
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressEditor;
